import { Table } from "reactstrap";
import { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { ToolContext } from "../context/ToolContext";
import { FormContext } from "../context/FormContext";
import { useAuth } from "../context/AuthContext";
import { clearResults } from "../app/networkSlice";
import { getResultProperties } from "../utils/referenceFunctions";
import CellEdit from "./CellEdit";
import { CellType } from "../constants/cellConst";
import TableHeader from "./ConfigTableHeader";
import { DropDownOption, DropDownOptionList, ConfigMapColumn } from "./config/ConfigDataMap";
import { CellValue, Page } from "./config/ConfigData";
import { miscEvent } from "../model/event/vndEvent";
import { logEvent } from "../services/apiService";
import {
  LocalOverrideConfigItemAsRecord,
  LocalOverrideConfigAsRecords,
} from "../utils/configurationFunctions";

interface ConfigTabProps {
  page: Page;
  theme: string;
  currentConfigIsApplied: boolean;
}

const ConfigTab = ({ page, theme, currentConfigIsApplied }: ConfigTabProps) => {
  const { formState, dispatch } = useContext(FormContext);
  const { toolState, setToolState } = useContext(ToolContext);
  const { localOverrideConfig, clientSettings } = formState as {
    localOverrideConfig: LocalOverrideConfigAsRecords;
    clientSettings: any;
  };
  const [sortColumn, setSortColumn] = useState<number>(-1);
  const [sortColumnAsc, setSortColumnAsc] = useState<boolean>(true);
  const [optionSearchValue, setOptionSearchValue] = useState<Record<number, string>>({});
  const dispatchRedux = useDispatch();
  const { getToken } = useAuth();

  const columnDataNames: string[] = page.columns.map((c: ConfigMapColumn) => c.dataName);
  const keyColumnDataNames: string[] = page.keyColumns;
  const overridableColumnDataNames: string[] = page.overridableColumns;
  const persistColumnDataNames: string[] = [...keyColumnDataNames, ...overridableColumnDataNames];

  const isAutoSelect = (colIndex: number): boolean => columnDataNames[colIndex] === "autoSelect";

  const getCellType = (row: CellValue[], colIndex: number): CellType => {
    if (
      page.optionLists.some((opt: DropDownOptionList) => opt.column === columnDataNames[colIndex])
    ) {
      return CellType.Dropdown;
    }
    if (page.nullables.some((name: string) => name === columnDataNames[colIndex])) {
      return CellType.NullOrZeroOrPositiveRational;
    }
    if (page.nullables.length) {
      if (page.nullables.includes(getRowKey(row))) {
        return CellType.NullOrZeroOrPositiveRational;
      }
    }
    // All cells are type ZeroOrPositiveRational by default unless configured otherwise
    return CellType.ZeroOrPositiveRational;
  };

  const hideResults = (): void => {
    const _toolState = toolState;
    _toolState.showResults = false;
    setToolState(_toolState);
    dispatchRedux(clearResults(getResultProperties(clientSettings)));
  };

  const getDropDownOptions = (colIndex: number): DropDownOption[] => {
    const optionsList = page.optionLists.find(
      (opt: DropDownOptionList) => opt.column === columnDataNames[colIndex],
    );
    if (optionsList) {
      return optionsList.options;
    }
    return [];
  };

  const getRowKey = (row: CellValue[]): string => {
    const keyValues = keyColumnDataNames.map((k: string) => row[columnDataNames.indexOf(k)]);
    const key = keyValues.join("-");
    return key;
  };

  const getGroupKey = (row: CellValue[]): string => {
    const keyValues = page.groupingColumns.map((k: string) => row[columnDataNames.indexOf(k)]);
    const key = keyValues.join("-");
    return key;
  };

  const getOverrideRowKey = (overrideRow: LocalOverrideConfigItemAsRecord): string => {
    const keyValues = keyColumnDataNames.map((k) => overrideRow[k]);
    const key = keyValues.join("-");
    return key;
  };

  const getResolvedValue = (row: CellValue[], colIndex: number): string => {
    const rowKey = getRowKey(row);
    const propertyName = columnDataNames[colIndex];
    let value = row[colIndex];

    if (currentConfigIsApplied && localOverrideConfig?.[page.dataName]?.length) {
      const overrideRow = localOverrideConfig[page.dataName]!.find(
        (r) => getOverrideRowKey(r) === rowKey,
      );
      if (overrideRow) {
        value = overrideRow[propertyName];
      }
    }
    return value?.toString() ?? "";
  };

  const applyCorrection = (
    row: CellValue[],
    colIndex: number,
    value: CellValue,
  ): { newValue: CellValue; warningMessage: string } => {
    const rowKey = getRowKey(row);
    const correctionLimit = page.correctionLimits.find((c) => c.editedRow === rowKey);
    if (correctionLimit) {
      const limitRow: CellValue[] | undefined = page.rows.find(
        (r) => getRowKey(r) === correctionLimit.limitRow,
      );
      if (!!limitRow) {
        const descriptionColumnIndex = columnDataNames.indexOf(correctionLimit.descriptionColumn);
        const limitDescription = limitRow[descriptionColumnIndex];
        const limitValue = getResolvedValue(limitRow, colIndex);
        if (correctionLimit.raiseToLimit) {
          if (Number(value) < Number(limitValue)) {
            return {
              newValue: limitValue,
              warningMessage: `The value cannot be less than ${limitDescription}. The value has been corrected.`,
            };
          }
        } else {
          if (Number(value) > Number(limitValue)) {
            return {
              newValue: limitValue,
              warningMessage: `The value cannot be more than ${limitDescription}. The value has been corrected.`,
            };
          }
        }
      }
    }
    return { newValue: value, warningMessage: "" };
  };

  const canOverrideCell = (colIndex: number): boolean => {
    if (!clientSettings.features.LocalOverrideConfigEnabled) {
      return false;
    }
    if (!currentConfigIsApplied) {
      return false;
    }
    const colDataname = columnDataNames[colIndex];
    if (!overridableColumnDataNames.includes(colDataname)) {
      return false;
    }
    return true;
  };

  const canEditCell = (row: CellValue[], colIndex: number): boolean => {
    if (page.overridableRows.length) {
      if (!page.overridableRows.includes(getRowKey(row))) {
        return false;
      }
    }
    if (isAutoSelect(colIndex)) {
      // Prevent the last Auto-Select item in a group to be turned off:
      if (getResolvedValue(row, colIndex) === "true") {
        const groupKey = getGroupKey(row);
        const allRowsInGroup = page.rows.filter((r) => getGroupKey(r) === groupKey);
        const noOfAutoSelects = allRowsInGroup.filter(
          (r) => getResolvedValue(r, colIndex) === "true",
        ).length;
        if (noOfAutoSelects <= 1) {
          return false;
        }
      }
    }

    return true;
  };

  const toOverrideRow = (tableRow: CellValue[]): LocalOverrideConfigItemAsRecord => {
    const overrideRow: LocalOverrideConfigItemAsRecord = {};
    columnDataNames.forEach((name, colIndex) => {
      if (persistColumnDataNames.includes(name)) {
        overrideRow[name] = tableRow[colIndex];
      }
    });
    return overrideRow;
  };

  useEffect(() => {
    setSortColumn(-1);
    setSortColumnAsc(true);
    setOptionSearchValue({});
  }, [page.title]);

  const sort = (columnIndex: number): void => {
    if (sortColumn === columnIndex) {
      setSortColumnAsc(!sortColumnAsc);
    } else {
      setSortColumn(columnIndex);
      setSortColumnAsc(true);
    }
  };

  const isExactMatch = (
    row: CellValue[],
    overrideRow: LocalOverrideConfigItemAsRecord,
  ): boolean => {
    return persistColumnDataNames.every(
      (propertyName) =>
        row[columnDataNames.indexOf(propertyName)]?.toString() ===
        overrideRow?.[propertyName]?.toString(),
    );
  };

  const orderedRows: CellValue[][] = [...page.rows];

  if (sortColumn !== -1) {
    orderedRows.sort((a, b) => {
      const valueA = a[sortColumn] ?? "";
      const valueB = b[sortColumn] ?? "";
      if (!sortColumnAsc) {
        return valueA < valueB ? 1 : valueA > valueB ? -1 : 0;
      }
      return valueA > valueB ? 1 : valueA < valueB ? -1 : 0;
    });
  }

  const filteredRows: CellValue[][] = orderedRows.filter((row: CellValue[]) => {
    return row.every((q, colIndex) => {
      const search = optionSearchValue[colIndex];
      if (search === undefined || search.trim() === "") {
        return true;
      }

      const cellValue: string = getResolvedValue(row, colIndex);
      const cellType: CellType = getCellType(row, colIndex);

      let cellText: string = "";
      switch (cellType) {
        case CellType.Dropdown:
          cellText =
            getDropDownOptions(colIndex).find((opt) => opt.value === cellValue)?.label ?? "";
          break;
        case CellType.ZeroOrPositiveRational:
          cellText = cellValue;
          break;
        case CellType.NullOrZeroOrPositiveRational:
          cellText = cellValue === "" ? "NULL" : cellValue;
          break;
      }

      if (cellText?.toLowerCase()?.includes(search.toLowerCase())) {
        return true;
      }
      return false;
    });
  });

  const updateDependantColumns = (
    row: CellValue[],
    overrideRow: LocalOverrideConfigItemAsRecord,
    colIndex: number,
    value: CellValue,
  ): void => {
    const columnDataName = columnDataNames[colIndex];
    const dependantColumns = page.dependantColumns.filter(
      (d) => d.sourceColumn === columnDataName && d.sourceValue === value,
    );
    dependantColumns.forEach((d) => {
      const defaultColumnIndex = columnDataNames.indexOf(d.defaultColumn);
      const defaultValue = row[defaultColumnIndex];
      overrideRow[d.targetColumn] = defaultValue;
    });
  };

  const handleSaveRow = async (
    row: CellValue[],
    colIndex: number,
    value: CellValue,
  ): Promise<string> => {
    let _localOverrideConfig = { ...localOverrideConfig };
    if (!_localOverrideConfig[page.dataName]) {
      _localOverrideConfig[page.dataName] = [];
    }
    const rowKey = getRowKey(row);
    let overrideRowIndex = _localOverrideConfig[page.dataName]!.findIndex(
      (r) => getOverrideRowKey(r) === rowKey,
    );
    const overrideRow: LocalOverrideConfigItemAsRecord =
      overrideRowIndex >= 0
        ? _localOverrideConfig[page.dataName]![overrideRowIndex]
        : toOverrideRow(row);

    const { newValue, warningMessage } = applyCorrection(row, colIndex, value);
    value = newValue;

    const propertyName = columnDataNames[colIndex];

    if (isAutoSelect(colIndex)) {
      overrideRow[propertyName] = value?.toString() === "true";
    } else {
      const cellType = getCellType(row, colIndex);

      switch (cellType) {
        case CellType.Dropdown:
          overrideRow[propertyName] = value?.toString() ?? "";
          break;
        case CellType.ZeroOrPositiveRational:
          overrideRow[propertyName] = Number(value);
          break;
        case CellType.NullOrZeroOrPositiveRational:
          overrideRow[propertyName] = value === null || value === "" ? null : Number(value);
          break;
        default:
          overrideRow[propertyName] = 0;
          break;
      }
    }

    updateDependantColumns(row, overrideRow, colIndex, value);

    if (isExactMatch(row, overrideRow)) {
      if (overrideRowIndex >= 0) {
        _localOverrideConfig[page.dataName]!.splice(overrideRowIndex, 1);
        if (!_localOverrideConfig[page.dataName]!.length) {
          delete _localOverrideConfig[page.dataName];
        }
      }
    } else {
      if (overrideRowIndex < 0) {
        _localOverrideConfig[page.dataName]!.push(overrideRow);
      }
    }

    hideResults();

    dispatch({
      form: "localOverrideConfig",
      obj: _localOverrideConfig,
      type: "REPLACE_STATE",
    });
    await logEvent(await getToken(), new miscEvent("LocalOverrideConfig"));
    return warningMessage;
  };

  const validate = (row: CellValue[], colIndex: number, value: string): boolean => {
    if (getCellType(row, colIndex) === CellType.Dropdown) {
      return true;
    }
    if (getCellType(row, colIndex) === CellType.ZeroOrPositiveRational) {
      if (value.match(/^\d[\d.]*$/)) {
        return true;
      }
    }
    if (getCellType(row, colIndex) === CellType.NullOrZeroOrPositiveRational) {
      if (value === "") {
        return true;
      }
      if (value.match(/^\d[\d.]*$/)) {
        return true;
      }
    }
    return false;
  };

  const handleRevertToDefault = async (row: CellValue[], colIndex: number): Promise<string> =>
    await handleSaveRow(row, colIndex, row[colIndex]);

  const getIsModified = (row: CellValue[], colIndex: number): boolean => {
    const rowKey = getRowKey(row);
    const propertyName = columnDataNames[colIndex];
    if (localOverrideConfig?.[page.dataName]?.length) {
      const overrideRow = localOverrideConfig[page.dataName]!.find(
        (r) => getOverrideRowKey(r) === rowKey,
      );
      if (overrideRow) {
        const modifiedValue = overrideRow[propertyName];
        if (
          (modifiedValue === null) !==
          (row[colIndex] === null || row[colIndex]?.toString() === "")
        ) {
          return true;
        }
        return modifiedValue?.toString() !== row[colIndex]?.toString();
      }
    }
    return false;
  };

  const getStyle = (row: CellValue[], colIndex: number): React.CSSProperties => {
    if (isAutoSelect(colIndex)) {
      if (getResolvedValue(row, colIndex) === "true") {
        return { color: "green" };
      }
    }
    return {};
  };

  return (
    <>
      <h5 className="mb-4">{page.title}</h5>
      <Table className={`table-sm table-custom table-custom-${theme} table-sortable`}>
        <thead>
          <tr className="mb-4">
            {page.columns
              .filter((c) => !c.hidden)
              .map((column) => (
                <TableHeader
                  key={column.displayName}
                  column={column}
                  page={page}
                  sort={sort}
                  sortColumn={sortColumn}
                  sortColumnAsc={sortColumnAsc}
                  optionSearchValue={optionSearchValue}
                  setOptionSearchValue={setOptionSearchValue}
                />
              ))}
          </tr>
        </thead>
        <tbody>
          {filteredRows.map((row, rowIndex) => (
            <tr key={`${getRowKey(row)}-${rowIndex}`}>
              {row.map(
                (cell, colIndex) =>
                  !page.columns[colIndex].hidden && (
                    <td key={colIndex}>
                      {canOverrideCell(colIndex) ? (
                        <CellEdit
                          key={`${getRowKey(row)}-${colIndex}`}
                          cellType={getCellType(row, colIndex)}
                          isModified={getIsModified(row, colIndex)}
                          canEditCell={canEditCell(row, colIndex)}
                          value={getResolvedValue(row, colIndex)}
                          dropDownOptions={getDropDownOptions(colIndex)}
                          viewStyle={getStyle(row, colIndex)}
                          handleRevertToDefault={async () =>
                            await handleRevertToDefault(row, colIndex)
                          }
                          handleValidate={(value) => validate(row, colIndex, value)}
                          handleSave={async (value) => await handleSaveRow(row, colIndex, value)}
                        />
                      ) : (
                        <>
                          <span style={getStyle(row, colIndex)}>
                            {getDropDownOptions(colIndex)?.find(
                              (opt) => opt.value === cell?.toString(),
                            )?.label ?? cell?.toString()}
                          </span>
                        </>
                      )}
                    </td>
                  ),
              )}
            </tr>
          ))}
        </tbody>
      </Table>
    </>
  );
};

export default ConfigTab;
